import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '../utils/logger';

const MicrophoneContext = createContext();

export const useMicrophone = () => {
  const context = useContext(MicrophoneContext);
  if (!context) {
    throw new Error('useMicrophone must be used within a MicrophoneProvider');
  }
  return context;
};

export const MicrophoneProvider = ({ children }) => {
  const [permissionState, setPermissionState] = useState('unknown'); // 'unknown', 'granted', 'denied', 'prompt'
  const [isRequesting, setIsRequesting] = useState(false);
  const [error, setError] = useState(null);
  const [stream, setStream] = useState(null);
  const [audioContext, setAudioContext] = useState(null);
  const [analyser, setAnalyser] = useState(null);
  const [hasLiveStream, setHasLiveStream] = useState(false);

  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const permissionStateRef = useRef(permissionState);
  const isRequestingRef = useRef(isRequesting);
  const isMicrophoneDesiredRef = useRef(false);

  // Update refs when state changes
  useEffect(() => {
    permissionStateRef.current = permissionState;
  }, [permissionState]);

  useEffect(() => {
    isRequestingRef.current = isRequesting;
  }, [isRequesting]);

  // Check current permission state
  const checkPermission = useCallback(async () => {
    if (!navigator.permissions) {
      // Fallback for browsers that don't support permissions API
      logger.info('🔍 Permissions API not supported, setting state to unknown');
      setPermissionState('unknown');
      return 'unknown';
    }

    try {
      const result = await navigator.permissions.query({ name: 'microphone' });
      logger.info('🔍 Current microphone permission state:', result.state);

      // Trust the Permissions API result; defer actual getUserMedia until phone off-hook
      setPermissionState(result.state);
      return result.state;
    } catch (error) {
      logger.warn('Could not check microphone permission:', error);
      setPermissionState('unknown');
      return 'unknown';
    }
  }, []);

  const trackEndHandlersRef = useRef(new Map());
  const trackRecoveryTimeoutRef = useRef(null);
  const requestPermissionRef = useRef(null);
  const cleanupRef = useRef(null);

  const clearTrackRecoveryTimeout = useCallback(() => {
    if (trackRecoveryTimeoutRef.current) {
      clearTimeout(trackRecoveryTimeoutRef.current);
      trackRecoveryTimeoutRef.current = null;
    }
  }, []);

  const detachTrackHandlers = useCallback((mediaStream) => {
    if (!mediaStream) return;

    mediaStream.getTracks().forEach(track => {
      const handler = trackEndHandlersRef.current.get(track);
      if (handler) {
        track.removeEventListener('ended', handler);
        trackEndHandlersRef.current.delete(track);
      }
    });
  }, []);

  // Clean up resources
  const cleanup = useCallback((reason = 'unknown') => {
    isMicrophoneDesiredRef.current = false;
    clearTrackRecoveryTimeout();

    logger.info(`🎤 Cleanup triggered (reason: ${reason})`);

    // Capture ref value to prevent race conditions/double cleanup
    const refStream = streamRef.current;

    // Cleanup stream from ref
    if (refStream) {
      logger.info(`🎤 Cleanup: Stopping ${refStream.getTracks().length} tracks from streamRef`);
      detachTrackHandlers(refStream);
      refStream.getTracks().forEach(track => {
        track.enabled = false;
        track.stop();
      });
      streamRef.current = null;
    }

    // Fallback: Cleanup stream from state if different from the ref we just cleaned
    // We compare against refStream (the value we captured) because streamRef.current is now null
    if (stream && stream !== refStream) {
      logger.info(`🎤 Cleanup: Stopping ${stream.getTracks().length} tracks from stream state (fallback)`);
      detachTrackHandlers(stream);
      stream.getTracks().forEach(track => {
        track.enabled = false;
        track.stop();
      });
    }

    if (audioContextRef.current) {
      logger.info('🎤 Cleanup: Closing AudioContext');
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setStream(null);
    setAudioContext(null);
    setAnalyser(null);
    setHasLiveStream(false);
    setStream(null);
    setAudioContext(null);
    setAnalyser(null);
    setHasLiveStream(false);
  }, [clearTrackRecoveryTimeout, detachTrackHandlers, stream]);

  // Keep cleanupRef up to date
  useEffect(() => {
    cleanupRef.current = cleanup;
  }, [cleanup]);

  const scheduleMicrophoneRecovery = useCallback((reason = 'unknown') => {
    if (trackRecoveryTimeoutRef.current) {
      logger.debug('🎤 Microphone recovery already scheduled - skipping duplicate request');
      return;
    }

    logger.warn(`🎤 Scheduling microphone recovery (reason: ${reason})`);

    trackRecoveryTimeoutRef.current = setTimeout(() => {
      trackRecoveryTimeoutRef.current = null;

      const isVisible = typeof document === 'undefined' ? true : document.visibilityState === 'visible';
      if (!isVisible) {
        logger.info('🎤 Page hidden - delaying microphone recovery until visible');
        return;
      }

      if (isRequestingRef.current) {
        logger.debug('🎤 Microphone request already in progress - skipping recovery start');
        return;
      }

      if (requestPermissionRef.current) {
        requestPermissionRef.current();
      }
    }, 600);
  }, []);

  const attachTrackRecoveryHandlers = useCallback((mediaStream) => {
    if (!mediaStream) return;

    detachTrackHandlers(mediaStream);

    mediaStream.getTracks().forEach(track => {
      const handleTrackEnded = () => {
        logger.warn(`🎤 Microphone track ended (state: ${track.readyState}) - attempting recovery`);
        setHasLiveStream(false);
        cleanup('track_ended');
        scheduleMicrophoneRecovery('track_ended');
      };

      trackEndHandlersRef.current.set(track, handleTrackEnded);
      track.addEventListener('ended', handleTrackEnded, { once: true });
    });

    const hasLiveTracks = mediaStream.getTracks().some(track => track.readyState === 'live');
    setHasLiveStream(hasLiveTracks);
  }, [cleanup, detachTrackHandlers, scheduleMicrophoneRecovery]);

  // Initialize permission check on mount
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  // Listen for permission changes
  useEffect(() => {
    if (!navigator.permissions) return;

    const handlePermissionChange = (event) => {
      setPermissionState(event.target.state);
    };

    navigator.permissions.query({ name: 'microphone' }).then(result => {
      result.addEventListener('change', handlePermissionChange);

      return () => {
        result.removeEventListener('change', handlePermissionChange);
      };
    });
  }, []);

  const requestPermission = useCallback(async () => {
    if (isRequesting) return false;

    // Check if we already have a stream - if so, clean it up first to prevent orphans
    if (streamRef.current || audioContextRef.current) {
      logger.warn('🎤 Found existing stream/context when requesting permission - cleaning up first');
      cleanup('stale_stream_found');
    }

    // Explicitly mark microphone as desired since we are requesting it
    // IMPORTANT: Must set this AFTER cleanup() because cleanup() sets it to false
    isMicrophoneDesiredRef.current = true;

    setIsRequesting(true);
    setError(null);
    setPermissionState('prompt');

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      // RACE CONDITION FIX: Check if microphone is still desired after the async getUserMedia call
      if (!isMicrophoneDesiredRef.current) {
        logger.warn('🎤 Microphone permission granted but no longer desired (race condition) - stopping tracks immediately');
        mediaStream.getTracks().forEach(track => track.stop());
        setPermissionState('granted'); // Technically granted, but we're not using it
        setIsRequesting(false);
        return false;
      }

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const analyserNode = audioCtx.createAnalyser();
      analyserNode.fftSize = 256;

      const source = audioCtx.createMediaStreamSource(mediaStream);
      source.connect(analyserNode);

      streamRef.current = mediaStream;
      audioContextRef.current = audioCtx;
      analyserRef.current = analyserNode;

      setStream(mediaStream);
      setAudioContext(audioCtx);
      setAnalyser(analyserNode);
      setPermissionState('granted');
      attachTrackRecoveryHandlers(mediaStream);

      logger.info('Microphone permission granted and initialized');
      return true;
    } catch (error) {
      logger.error('Failed to get microphone permission:', error);
      logger.error('Error details:', {
        name: error.name,
        message: error.message,
        code: error.code
      });

      let errorMessage = error.message;
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Microphone access was denied. Please allow microphone access in your browser settings and try again.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No microphone found. Please connect a microphone and try again.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Microphone is being used by another application. Please close other applications and try again.';
      }

      setError(errorMessage);
      setPermissionState('denied');
      setHasLiveStream(false);
      return false;
    } finally {
      setIsRequesting(false);
    }
  }, [attachTrackRecoveryHandlers, isRequesting, cleanup]);

  useEffect(() => {
    requestPermissionRef.current = requestPermission;
  }, [requestPermission]);

  // Listen for microphone access requests from phone off-hook
  useEffect(() => {
    const handleMicrophoneRequest = (event) => {
      const currentPermissionState = permissionStateRef.current;
      const currentIsRequesting = isRequestingRef.current;

      logger.info('🎤 Microphone access requested:', event.detail);
      logger.info('🎤 Current permission state:', currentPermissionState);
      logger.info('🎤 Is requesting:', currentIsRequesting);

      // Only respond to phone off-hook events, not test events
      if (event.detail?.reason === 'phoneOffHook' && !currentIsRequesting) {
        // Mark that we want the microphone
        isMicrophoneDesiredRef.current = true;

        if (currentPermissionState === 'denied') {
          logger.warn('🎤 Microphone permission was previously denied. Please reset browser permissions:');
          logger.warn('🎤 Chrome: Click lock icon → Site settings → Microphone → Allow');
          logger.warn('🎤 Firefox: Click shield icon → Permissions → Microphone → Allow');
          logger.warn('🎤 Then refresh the page and try again.');
        } else if (currentPermissionState === 'granted' && !streamRef.current) {
          // Permission is granted but we don't have a stream yet - set it up now
          logger.info('🎤 Permission granted but no stream - setting up microphone for recording');
          if (requestPermissionRef.current) {
            requestPermissionRef.current();
          }
        } else if (currentPermissionState !== 'granted') {
          logger.info('🎤 Requesting microphone permission due to phone off-hook');
          if (requestPermissionRef.current) {
            requestPermissionRef.current();
          }
        } else {
          logger.debug('🎤 Microphone already set up and ready');
        }
      } else {
        logger.debug('🎤 Skipping microphone request - not phone off-hook or already requesting');
      }
    };

    logger.debug('🎤 Setting up microphone request event listener');
    window.addEventListener('requestMicrophoneAccess', handleMicrophoneRequest);

    return () => {
      logger.debug('🎤 Removing microphone request event listener');
      window.removeEventListener('requestMicrophoneAccess', handleMicrophoneRequest);
    };
  }, []); // Remove requestPermission dependency to prevent re-binding loop

  // Listen for microphone release requests (phone on-hook)
  useEffect(() => {
    const handleMicrophoneRelease = (event) => {
      logger.debug('🎤 Microphone release requested:', event.detail);
      cleanup('release_event');
    };

    window.addEventListener('releaseMicrophoneAccess', handleMicrophoneRelease);
    return () => {
      window.removeEventListener('releaseMicrophoneAccess', handleMicrophoneRelease);
    };
  }, [cleanup]);

  // Attempt to recover microphone when returning to the page
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      const hasLiveTracks = streamRef.current?.getTracks()?.some(track => track.readyState === 'live');
      if (hasLiveTracks) {
        // ... (keep logic logic if any, but snippet below was simple)
      }
      if (!hasLiveTracks && permissionStateRef.current === 'granted' && !isRequestingRef.current) {
        logger.info('🎤 Page visible but microphone tracks are ended - attempting recovery');
        cleanup('visibility_recovery');
        scheduleMicrophoneRecovery('visibility_change');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [cleanup, scheduleMicrophoneRecovery]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current('unmount');
      }
    };
  }, []);

  const value = {
    permissionState,
    isRequesting,
    error,
    stream,
    audioContext,
    analyser,
    requestPermission,
    checkPermission,
    cleanup,
    hasLiveStream
  };

  return (
    <MicrophoneContext.Provider value={value}>
      {children}
    </MicrophoneContext.Provider>
  );
};
